import { useState, useEffect, useCallback } from "react";

// Reihenfolge bewusst: Hydro Tower zuerst (links), Homebox danach (rechts).
const SYSTEMS = {
  tower: {
    id: "tower",
    name: "Hydro Tower",
    subtitle: "Behälter 39 × 39 cm",
    color: "#2563A8",
    light: "#D6E4F7",
    volFormula: (h) => +(39 * 39 * h / 1000).toFixed(2),
  },
  homebox: {
    id: "homebox",
    name: "Homebox",
    subtitle: "RDWC · 5 Zylinder Ø 19 cm",
    color: "#2E7D52",
    light: "#D6EAD8",
    volFormula: (h) => +(5 * Math.PI * Math.pow(9.5, 2) * h / 1000).toFixed(2),
  },
};

// Ein Eintrag = ein System an einem Datum. ID = `${date}_${system}`.
function emptyEntry(system = "tower") {
  return {
    date: new Date().toISOString().slice(0, 10),
    system,
    waterLevel: "", volume: "", ec: "", temp: "", ph: "",
    actions: { water: { action: "", after: "" }, ec: { action: "", after: "" }, ph: { action: "", after: "" } },
  };
}
function entryId(e) { return `${e.date}_${e.system}`; }

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }
function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

// ── API helpers ───────────────────────────────────────────────
async function api(method, path, body) {
  const res = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  return res.json();
}

// ── Leaf SVG ──────────────────────────────────────────────────
const LeafIcon = ({ size = 20, color = "#2E7D52" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2C6.5 2 3 7 3 12c0 3.5 2 6.5 5 8l1-3c-1.5-1-2.5-2.8-2.5-5 0-3.5 2.5-7 5.5-7s5.5 3.5 5.5 7c0 2.2-1 4-2.5 5l1 3c3-1.5 5-4.5 5-8 0-5-3.5-10-9-10z" fill={color} />
  </svg>
);

// ── Login screen ──────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin() {
    setLoading(true);
    setError("");
    const res = await api("POST", "/api/login", { password: pw });
    if (res.ok) {
      onLogin();
    } else {
      setError("Falsches Passwort");
      setLoading(false);
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f4f5f0", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 32, width: 320, boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <LeafIcon size={28} color="#2E7D52" />
          <div>
            <div style={{ fontWeight: 800, fontSize: 20, color: "#1B4D2E" }}>Hydroponik</div>
            <div style={{ fontSize: 12, color: "#999" }}>Messprotokoll</div>
          </div>
        </div>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>Passwort</label>
        <input
          type="password"
          value={pw}
          onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          placeholder="••••••••"
          style={{ width: "100%", padding: "12px", fontSize: 16, borderRadius: 10, border: "1.5px solid #e0e0d8", marginBottom: 12, boxSizing: "border-box", fontFamily: "'DM Mono', monospace" }}
          autoFocus
        />
        {error && <div style={{ color: "#cc4444", fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <button onClick={handleLogin} disabled={loading} style={{
          width: "100%", padding: 14, background: "#2E7D52", color: "#fff", border: "none",
          borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif", opacity: loading ? 0.7 : 1,
        }}>
          {loading ? "…" : "Anmelden"}
        </button>
      </div>
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────
function Field({ label, value, onChange, type = "number", unit, placeholder, wide, last }) {
  const hasLast = last !== undefined && last !== null && last !== "";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: wide ? "1 1 100%" : "1 1 80px", minWidth: wide ? "100%" : 70 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          type={type === "number" ? "number" : "text"}
          inputMode={type === "number" ? "decimal" : "text"}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder || "—"}
          style={{
            width: "100%", padding: unit ? "10px 28px 10px 10px" : "10px",
            fontSize: 15, fontFamily: "'DM Mono', monospace",
            background: "#f8f8f6", border: "1.5px solid #e0e0d8",
            borderRadius: 8, color: "#1a1a1a", outline: "none", boxSizing: "border-box",
          }}
        />
        {unit && <span style={{ position: "absolute", right: 8, fontSize: 10, color: "#999", pointerEvents: "none" }}>{unit}</span>}
      </div>
      {hasLast && (
        <span style={{ fontSize: 10, color: "#aaa", fontFamily: "'DM Mono', monospace" }}>
          zuletzt: {last}{unit ? ` ${unit}` : ""}
        </span>
      )}
    </div>
  );
}

// ── SystemCard ────────────────────────────────────────────────
function SystemCard({ sys, data, onChange, last }) {
  const vol = data.waterLevel !== "" ? sys.volFormula(parseFloat(data.waterLevel) || 0) : "";
  const lastVol = last && last.waterLevel !== "" && last.waterLevel !== undefined
    ? sys.volFormula(parseFloat(last.waterLevel) || 0) : "";
  function setField(key, val) {
    const u = deepClone(data);
    u[key] = val;
    if (key === "waterLevel") u.volume = val !== "" ? sys.volFormula(parseFloat(val) || 0) : "";
    onChange(u);
  }
  function setAction(param, key, val) {
    const u = deepClone(data);
    u.actions[param][key] = val;
    onChange(u);
  }
  return (
    <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: 16 }}>
      <div style={{ background: sys.color, padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
        <LeafIcon size={18} color="rgba(255,255,255,0.8)" />
        <div>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{sys.name}</div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 10 }}>{sys.subtitle}</div>
        </div>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: sys.color, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Messwerte</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          <Field label="Wasserstand" value={data.waterLevel} onChange={v => setField("waterLevel", v)} unit="cm" last={last?.waterLevel} />
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 80px", minWidth: 70 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>Volumen</label>
            <div style={{ padding: "10px", background: "#f0f0ee", borderRadius: 8, fontSize: 15, fontFamily: "'DM Mono', monospace", color: "#555", border: "1.5px solid #e0e0d8" }}>
              {vol !== "" ? `${vol} L` : "—"}
            </div>
            {lastVol !== "" && <span style={{ fontSize: 10, color: "#aaa", fontFamily: "'DM Mono', monospace" }}>zuletzt: {lastVol} L</span>}
          </div>
          <Field label="EC-Wert" value={data.ec} onChange={v => setField("ec", v)} unit="µS" last={last?.ec} />
          <Field label="Temperatur" value={data.temp} onChange={v => setField("temp", v)} unit="°C" last={last?.temp} />
          <Field label="pH-Wert" value={data.ph} onChange={v => setField("ph", v)} last={last?.ph} />
        </div>
        {["water", "ec", "ph"].map(param => {
          const labels = { water: "💧 Wasser", ec: "⚡ EC", ph: "🧪 pH" };
          const afterUnit = { water: "cm", ec: "µS", ph: "" };
          const hints = { water: "z.B. 5 L aufgefüllt", ec: "z.B. Dünger 20 ml", ph: "z.B. pH-Down 2 ml" };
          const lastAfter = last?.actions?.[param]?.after;
          return (
            <div key={param} style={{ background: "#fafaf8", borderRadius: 10, padding: 12, marginBottom: 8, border: "1px solid #ececea" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{labels[param]} – Maßnahme</div>
              <Field label="Maßnahme" value={data.actions[param].action} onChange={v => setAction(param, "action", v)} type="text" placeholder={hints[param]} wide />
              <div style={{ marginTop: 8 }}>
                <Field label="Wert danach" value={data.actions[param].after} onChange={v => setAction(param, "after", v)} unit={afterUnit[param]} last={lastAfter} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Badge / ActionLine (shared) ───────────────────────────────
function Badge({ label, value, unit, color }) {
  if (value === "" || value === null || value === undefined) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, background: color + "20", borderRadius: 6, padding: "2px 7px", fontSize: 12, color, fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>
      {label && <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>{label}</span>}
      {value}{unit ? <span style={{ fontSize: 10, opacity: 0.7 }}>{unit}</span> : ""}
    </span>
  );
}
function ActionLine({ param, actions, color }) {
  const a = actions?.[param];
  if (!a?.action && !a?.after) return null;
  const labels = { water: "💧", ec: "⚡", ph: "🧪" };
  return (
    <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>
      {labels[param]} {a.action && <span style={{ color: "#333" }}>{a.action}</span>}
      {a.after && <span style={{ color }}> → {a.after}</span>}
    </div>
  );
}

// ── HistoryRow (ein System-Eintrag) ───────────────────────────
function HistoryRow({ entry, onDelete, onEdit }) {
  const [open, setOpen] = useState(false);
  const sys = SYSTEMS[entry.system] || SYSTEMS.homebox;
  return (
    <div style={{ background: "#fff", borderRadius: 12, marginBottom: 10, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", borderLeft: `4px solid ${sys.color}` }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>{formatDate(entry.date)}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
            {entry.ec && <span style={{ fontSize: 11, background: sys.light, color: sys.color, borderRadius: 5, padding: "2px 6px", fontFamily: "'DM Mono', monospace" }}>EC {entry.ec}</span>}
            {entry.ph && <span style={{ fontSize: 11, background: sys.light, color: sys.color, borderRadius: 5, padding: "2px 6px", fontFamily: "'DM Mono', monospace" }}>pH {entry.ph}</span>}
          </div>
        </div>
        <span style={{ color: "#ccc", fontSize: 18 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid #f0f0ee", padding: "14px 14px 10px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
            <Badge label="🌊" value={entry.waterLevel} unit="cm" color={sys.color} />
            <Badge label="📦" value={entry.volume} unit="L" color={sys.color} />
            <Badge label="EC" value={entry.ec} unit="µS" color={sys.color} />
            {entry.temp !== "" && <Badge label="🌡" value={entry.temp} unit="°C" color="#888" />}
            <Badge label="pH" value={entry.ph} color="#8B4513" />
          </div>
          <ActionLine param="water" actions={entry.actions} color={sys.color} />
          <ActionLine param="ec" actions={entry.actions} color={sys.color} />
          <ActionLine param="ph" actions={entry.actions} color={sys.color} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={() => onEdit(entry)} style={{ background: "none", border: "1px solid #2E7D52", color: "#2E7D52", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>✏️ Bearbeiten</button>
            <button onClick={() => onDelete(entryId(entry))} style={{ background: "none", border: "1px solid #ffcccc", color: "#cc4444", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer" }}>Löschen</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── effectiveVal (ein System-Eintrag) ─────────────────────────
function effectiveVal(entry, param) {
  const actionKey = { ec: "ec", ph: "ph", waterLevel: "water", volume: "water" }[param];
  if (actionKey) {
    const after = entry.actions?.[actionKey]?.after;
    if (after !== "" && after !== null && after !== undefined && !isNaN(parseFloat(after))) {
      if (param === "volume") {
        const f = (SYSTEMS[entry.system] || SYSTEMS.homebox).volFormula;
        return f(parseFloat(after));
      }
      return parseFloat(after);
    }
  }
  return parseFloat(entry[param]);
}

// ── MiniChart (entries bereits auf ein System gefiltert) ──────
function MiniChart({ entries, param, color, label }) {
  const vals = entries
    .map(e => ({ date: formatDate(e.date), val: effectiveVal(e, param) }))
    .filter(v => !isNaN(v.val));
  if (vals.length < 2) return null;
  const min = Math.min(...vals.map(v => v.val));
  const max = Math.max(...vals.map(v => v.val));
  const range = max - min || 1;
  const W = 280, H = 60, PAD = 8;
  const xStep = (W - PAD * 2) / (vals.length - 1);
  const points = vals.map((v, i) => ({
    x: PAD + i * xStep,
    y: H - PAD - ((v.val - min) / range) * (H - PAD * 2),
    val: v.val, date: v.date,
  }));
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "12px 14px", marginBottom: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill={color} />
            <text x={p.x} y={H - 1} textAnchor="middle" fontSize="7" fill="#aaa">{p.date.slice(0, 5)}</text>
            <text x={p.x} y={p.y - 7} textAnchor="middle" fontSize="8" fill={color} fontWeight="700">{p.val}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── WaterConsumptionChart (entries bereits auf ein System gefiltert) ──
function WaterConsumptionChart({ entries, color, label }) {
  const sorted = [...entries]
    .filter(e => !isNaN(effectiveVal(e, "volume")))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length < 2) return null;
  const bars = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1], curr = sorted[i];
    const volPrev = effectiveVal(prev, "volume");
    const volCurr = effectiveVal(curr, "volume");
    const cmPrev  = effectiveVal(prev, "waterLevel");
    const cmCurr  = effectiveVal(curr, "waterLevel");
    if (isNaN(volPrev) || isNaN(volCurr)) continue;
    const days = Math.round((new Date(curr.date) - new Date(prev.date)) / 86400000);
    if (days <= 0) continue;
    bars.push({
      label: formatDate(curr.date).slice(0, 5), days,
      lPerDay:  +((volPrev - volCurr) / days).toFixed(2),
      cmPerDay: isNaN(cmPrev) || isNaN(cmCurr) ? null : +((cmPrev - cmCurr) / days).toFixed(2),
    });
  }
  if (bars.length === 0) return null;
  const W = 280, H = 100, PAD = 8, BAR_PAD = 8, LABEL_H = 22;
  const chartH = H - LABEL_H;
  const groupW = (W - PAD * 2) / bars.length;
  const barW = (groupW - BAR_PAD) / 2;
  const maxL  = Math.max(...bars.map(b => Math.abs(b.lPerDay)), 0.1);
  const maxCm = Math.max(...bars.map(b => Math.abs(b.cmPerDay ?? 0)), 0.1);
  const colorCm = color + "88";
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "12px 14px", marginBottom: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
        {[["L / Tag", color], ["cm / Tag", "#888"], ["Aufgefüllt", "#e07020"]].map(([l, c]) => (
          <span key={l} style={{ fontSize: 10, color: c, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: c }} />{l}
          </span>
        ))}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        {bars.map((b, i) => {
          const gx = PAD + i * groupW;
          const lNeg = b.lPerDay < 0;
          const lH = Math.max(2, (Math.abs(b.lPerDay) / maxL) * (chartH - 18));
          const lY = chartH - lH;
          const lCol = lNeg ? "#e07020" : color;
          const cmNeg = (b.cmPerDay ?? 0) < 0;
          const cmH = b.cmPerDay != null ? Math.max(2, (Math.abs(b.cmPerDay) / maxCm) * (chartH - 18)) : 0;
          const cmY = chartH - cmH;
          const cmCol = cmNeg ? "#f0a060" : colorCm;
          return (
            <g key={i}>
              <rect x={gx} y={lY} width={barW} height={lH} rx={2} fill={lCol} />
              <text x={gx + barW / 2} y={lY - 2} textAnchor="middle" fontSize="7.5" fill={lCol} fontWeight="700">{Math.abs(b.lPerDay)}</text>
              {b.cmPerDay != null && <>
                <rect x={gx + barW + 2} y={cmY} width={barW} height={cmH} rx={2} fill={cmCol} />
                <text x={gx + barW + 2 + barW / 2} y={cmY - 2} textAnchor="middle" fontSize="7.5" fill="#888" fontWeight="700">{Math.abs(b.cmPerDay)}</text>
              </>}
              <text x={gx + groupW / 2 - BAR_PAD / 2} y={chartH + 9} textAnchor="middle" fontSize="7" fill="#aaa">{b.label}</text>
              <text x={gx + groupW / 2 - BAR_PAD / 2} y={chartH + 17} textAnchor="middle" fontSize="6" fill="#ccc">{b.days}d</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── PasswordModal ─────────────────────────────────────────────
function PasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  async function save() {
    setErr("");
    if (nw.length < 6) { setErr("Neues Passwort muss mind. 6 Zeichen haben"); return; }
    if (nw !== nw2) { setErr("Die neuen Passwörter stimmen nicht überein"); return; }
    setLoading(true);
    const res = await api("POST", "/api/password", { currentPassword: cur, newPassword: nw });
    setLoading(false);
    if (res.ok) { setOk(true); setTimeout(onClose, 1200); }
    else { setErr(res.error || "Fehler beim Ändern"); }
  }
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 340, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: "#1B4D2E", marginBottom: 16 }}>Passwort ändern</div>
        {ok ? (
          <div style={{ color: "#2E7D52", fontWeight: 700, fontSize: 14, padding: "8px 0" }}>✓ Passwort geändert</div>
        ) : (
          <>
            {[["Aktuelles Passwort", cur, setCur], ["Neues Passwort", nw, setNw], ["Neues Passwort bestätigen", nw2, setNw2]].map(([label, val, set]) => (
              <div key={label} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>{label}</label>
                <input type="password" value={val} onChange={e => set(e.target.value)} onKeyDown={e => e.key === "Enter" && save()}
                  style={{ width: "100%", padding: "10px", fontSize: 15, borderRadius: 10, border: "1.5px solid #e0e0d8", boxSizing: "border-box", fontFamily: "'DM Mono', monospace" }} />
              </div>
            ))}
            {err && <div style={{ color: "#cc4444", fontSize: 13, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button onClick={onClose} style={{ flex: 1, padding: 12, background: "#f0f0ee", color: "#555", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Abbrechen</button>
              <button onClick={save} disabled={loading} style={{ flex: 1, padding: 12, background: "#2E7D52", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>{loading ? "…" : "Speichern"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState("new");
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyEntry());
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Check auth on mount
  useEffect(() => {
    api("GET", "/api/me").then(res => {
      setAuthenticated(!!res.authenticated);
      setAuthChecked(true);
    });
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    const data = await api("GET", "/api/entries");
    setEntries(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authenticated) loadEntries();
  }, [authenticated, loadEntries]);

  function selectSystem(system) {
    setForm(f => {
      const u = deepClone(f);
      u.system = system;
      u.volume = u.waterLevel !== "" ? SYSTEMS[system].volFormula(parseFloat(u.waterLevel) || 0) : "";
      return u;
    });
  }

  async function handleSubmit() {
    const entry = deepClone(form);
    const sys = SYSTEMS[entry.system] || SYSTEMS.tower;
    if (entry.waterLevel !== "") entry.volume = sys.volFormula(parseFloat(entry.waterLevel));
    const newId = entryId(entry);
    // Falls beim Bearbeiten Datum/System geändert wurde: alten Eintrag entfernen.
    if (editingId && editingId !== newId) await api("DELETE", `/api/entries/${editingId}`);
    await api("PUT", `/api/entries/${newId}`, entry);
    await loadEntries();
    setForm(emptyEntry(entry.system));
    setEditingId(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setTab("history");
  }

  async function handleDelete(id) {
    await api("DELETE", `/api/entries/${id}`);
    await loadEntries();
  }

  function handleEdit(entry) {
    setForm(deepClone(entry));
    setEditingId(entryId(entry));
    setTab("new");
  }

  function handleCancelEdit() {
    setForm(emptyEntry(form.system));
    setEditingId(null);
  }

  async function handleLogout() {
    await api("POST", "/api/logout");
    setAuthenticated(false);
  }

  if (!authChecked) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'DM Sans', sans-serif", color: "#2E7D52" }}>Lade…</div>;
  if (!authenticated) return <LoginScreen onLogin={() => { setAuthenticated(true); }} />;

  const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));
  const hbEntries = sorted.filter(e => e.system === "homebox");
  const twEntries = sorted.filter(e => e.system === "tower");
  const activeSys = SYSTEMS[form.system] || SYSTEMS.tower;
  // Letzte Messung des gewählten Systems VOR dem Formular-Datum (für „zuletzt"-Hinweise)
  const lastEntry = entries
    .filter(e => e.system === form.system && entryId(e) !== editingId && e.date < form.date)
    .sort((a, b) => b.date.localeCompare(a.date))[0] || null;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: "#f4f5f0", minHeight: "100vh", maxWidth: 480, margin: "0 auto" }}>
      {showPw && <PasswordModal onClose={() => setShowPw(false)} />}
      <div style={{ background: "#1B4D2E", padding: "20px 16px 16px", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <LeafIcon size={22} color="#7dba8f" />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, lineHeight: 1 }}>Hydroponik</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>Messprotokoll</div>
          </div>
          {saved && <span style={{ marginLeft: "auto", background: "#2E7D52", color: "#fff", borderRadius: 8, padding: "4px 10px", fontSize: 12 }}>✓ Gespeichert</span>}
          <button onClick={() => setShowPw(true)} style={{ marginLeft: "auto", background: "none", border: "1px solid rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.6)", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Passwort</button>
          <button onClick={handleLogout} style={{ background: "none", border: "1px solid rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.6)", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Abmelden</button>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[["new","+ Neu"], ["history","Verlauf"], ["charts","Grafiken"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex: 1, padding: "8px 4px", borderRadius: 10, border: "none", cursor: "pointer",
              fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
              background: tab === id ? "#fff" : "rgba(255,255,255,0.12)",
              color: tab === id ? "#1B4D2E" : "rgba(255,255,255,0.7)",
            }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "16px 14px 80px" }}>
        {tab === "new" && (
          <>
            {editingId && (
              <div style={{ background: "#fff8e1", border: "1.5px solid #f0c040", borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#7a5800" }}>✏️ {activeSys.name} vom {formatDate(form.date)} bearbeiten</span>
                <button onClick={handleCancelEdit} style={{ background: "none", border: "none", color: "#999", fontSize: 13, cursor: "pointer" }}>Abbrechen</button>
              </div>
            )}
            {/* System-Auswahl */}
            <label style={{ fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>System</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {Object.values(SYSTEMS).map(s => {
                const active = form.system === s.id;
                return (
                  <button key={s.id} onClick={() => selectSystem(s.id)} disabled={!!editingId} style={{
                    flex: 1, padding: "12px 8px", borderRadius: 12, cursor: editingId ? "not-allowed" : "pointer",
                    border: active ? `2px solid ${s.color}` : "1.5px solid #e0e0d8",
                    background: active ? s.light : "#fff", textAlign: "left",
                    opacity: editingId && !active ? 0.4 : 1,
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <LeafIcon size={18} color={s.color} />
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: active ? s.color : "#444" }}>{s.name}</div>
                      <div style={{ fontSize: 9, color: "#999" }}>{s.subtitle}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Datum</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                style={{ padding: "10px 12px", fontSize: 15, borderRadius: 10, border: "1.5px solid #e0e0d8", background: "#fff", width: "100%", boxSizing: "border-box", fontFamily: "'DM Mono', monospace" }} />
            </div>
            {lastEntry && (
              <div style={{ fontSize: 11, color: "#999", marginBottom: 8 }}>
                „zuletzt"-Werte von der Messung am <strong style={{ color: activeSys.color }}>{formatDate(lastEntry.date)}</strong>
              </div>
            )}
            <SystemCard sys={activeSys} data={form} onChange={d => setForm({ ...d, date: form.date, system: form.system })} last={lastEntry} />
            <button onClick={handleSubmit} style={{
              width: "100%", padding: "16px", background: activeSys.color, color: "#fff", border: "none", borderRadius: 14,
              fontSize: 16, fontWeight: 800, cursor: "pointer", boxShadow: `0 4px 16px ${activeSys.color}59`,
            }}>
              {editingId ? "Änderungen speichern" : `${activeSys.name}-Eintrag speichern`}
            </button>
          </>
        )}
        {tab === "history" && (
          <>
            {loading ? <div style={{ textAlign: "center", color: "#aaa", marginTop: 40 }}>Lade…</div> : (
              <>
                {entries.length === 0 && <div style={{ color: "#aaa", textAlign: "center", marginTop: 40 }}>Noch keine Einträge</div>}
                {[["tower", twEntries], ["homebox", hbEntries]].map(([sysId, list]) => {
                  const s = SYSTEMS[sysId];
                  return (
                    <div key={sysId} style={{ marginBottom: 24 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                        <LeafIcon size={16} color={s.color} />
                        <span style={{ fontSize: 13, fontWeight: 800, color: s.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.name}</span>
                        <span style={{ fontSize: 11, color: "#aaa" }}>· {list.length} Einträge</span>
                      </div>
                      {list.length === 0
                        ? <div style={{ color: "#bbb", fontSize: 13, padding: "4px 2px 8px" }}>Noch keine Einträge für {s.name}</div>
                        : list.map(e => <HistoryRow key={entryId(e)} entry={e} onDelete={handleDelete} onEdit={handleEdit} />)}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
        {tab === "charts" && (
          <>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 12 }}>Verlauf der letzten Messungen</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#2563A8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Hydro Tower</div>
            <MiniChart entries={twEntries} param="ec" color="#2563A8" label="EC-Wert (µS/cm)" />
            <MiniChart entries={twEntries} param="ph" color="#1a4a8a" label="pH-Wert" />
            <MiniChart entries={twEntries} param="waterLevel" color="#4a8acc" label="Wasserstand (cm)" />
            <div style={{ fontSize: 11, fontWeight: 700, color: "#2E7D52", textTransform: "uppercase", letterSpacing: "0.08em", margin: "16px 0 8px" }}>Homebox</div>
            <MiniChart entries={hbEntries} param="ec" color="#2E7D52" label="EC-Wert (µS/cm)" />
            <MiniChart entries={hbEntries} param="ph" color="#5a8a3a" label="pH-Wert" />
            <MiniChart entries={hbEntries} param="waterLevel" color="#3aaa6a" label="Wasserstand (cm)" />
            <div style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.08em", margin: "16px 0 8px" }}>💧 Wasserverbrauch</div>
            <WaterConsumptionChart entries={twEntries} color="#2563A8" label="Tower – Verbrauch pro Tag" />
            <WaterConsumptionChart entries={hbEntries} color="#2E7D52" label="Homebox – Verbrauch pro Tag" />
          </>
        )}
      </div>
    </div>
  );
}
